import {
  Stack,
  Flex,
  Title,
  MantineTheme,
  useMantineTheme,
  Paper,
  StackProps,
  PaperProps,
  Box,
  ModalProps,
} from "@mantine/core"
import { SuspenseWithErrorBoundary } from "../../../../-components/suspense"
import classes from "./index.module.css"

import { PropsWithChildren } from "react"
import { ellipsify } from "../../../../utils/ellipsify"
import { NavigateOptions } from "@tanstack/react-router"
import { Link } from "../../../../-components/link"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { ResponsiveSpoiler } from "@components/responsive-spoiler"
import { useTranslation } from "react-i18next"
import { DefaultModal } from "@components/default-modal"
import { useDisclosure } from "@mantine/hooks"

export interface DashboardCardProps extends PropsWithChildren, PaperProps {
  leftOfTitle?: React.ReactNode
  rightOfTitle?: React.ReactNode
  title?: string
  titleLinksTo?: NavigateOptions
  color?: (theme: MantineTheme) => string
  onClick?: () => void
  stackProps?: StackProps
  contentInSpoiler?: boolean
  modalProps?: Omit<ModalProps, "opened" | "onClose" | "title">
}

export function DashboardCard({
  title,
  titleLinksTo,
  leftOfTitle,
  rightOfTitle,
  color,
  onClick,
  children,
  stackProps,
  contentInSpoiler,
  modalProps,
  ...props
}: DashboardCardProps) {
  const theme = useMantineTheme()
  const { xs } = useBreakpoints()
  const contentRef = React.useRef<HTMLDivElement>(null)
  const { t } = useTranslation("app_server")
  const [opened, { open, close }] = useDisclosure()

  const Content = React.memo(
    ({ inModal, ...props }: { inModal?: boolean } & StackProps) => (
      <Stack align="flex-start" {...stackProps} {...props}>
        {title && !inModal ? (
          <Flex gap="xs" justify={"start"} w="100%" align="center">
            {leftOfTitle}

            {titleLinksTo ? (
              <Link
                fw={700}
                size="lg"
                navigateOptions={titleLinksTo}
                linkText={title}
              />
            ) : (
              <Title style={{ ...ellipsify }} order={4}>
                {title}
              </Title>
            )}
            <div style={{ flexGrow: 1 }} />
            {rightOfTitle}
          </Flex>
        ) : null}
        <SuspenseWithErrorBoundary>{children}</SuspenseWithErrorBoundary>
      </Stack>
    )
  )

  return (
    <Paper
      bg={color && color(theme)}
      withBorder
      p="sm"
      radius="md"
      shadow="sm"
      miw={0}
      mih={xs ? 0 : 400}
      h={xs ? undefined : "45dvh"}
      mah={xs ? "75dvh" : undefined}
      style={{ alignItems: "center" }}
      className={onClick ? classes.cardButton : undefined}
      onClick={onClick}
      {...props}
    >
      {contentInSpoiler ? (
        <>
          <ResponsiveSpoiler
            h={"100%"}
            showMoreLabel={t("ShowMore")}
            expanded={false}
            onExpandedClick={open}
            flex={1}
            contentHeight={contentRef.current?.scrollHeight}
          >
            <Box w={"100%"} ref={contentRef}>
              <Content />
            </Box>
          </ResponsiveSpoiler>
          <DefaultModal
            title={title}
            opened={opened}
            onClose={close}
            {...modalProps}
          >
            <Content inModal />
          </DefaultModal>
        </>
      ) : (
        <Content mih={0} h="100%" />
      )}
    </Paper>
  )
}
